import { useMemo } from 'react';
import {
  useFilterQueryState,
  UseFilterQueryStateType,
} from 'src/components/MainFilterBar/useFilterQueryState';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { EventWithData } from 'src/WebApiController';
import {
  ActionOutboxEntityType,
  Listing,
  ListingQuery,
} from 'src/WebApiController';

interface UseInventoryEventBulkActionsState {
  /**
   * Whether all **visible** listings are selected.
   * Even if set to true, some listings can be hidden by table column filters.
   */
  isAllListingsSelected: boolean;
  affectedVisibleListingIds: number[];
  filterQueryWithEventIds: UseFilterQueryStateType<ListingQuery>;
  /**
   * Whether the current bulk selection includes any placeholder event
   * (Feature.PlaceholderEventGroup). `event.placeholders` is non-empty only for
   * synthesized placeholder events, so it is the authoritative discriminator —
   * the same one `searchEvents` uses for the placeholder quick filter. Actions
   * that can't operate on placeholders (e.g. Retail Sale) use this to disable
   * themselves.
   */
  hasSelectedPlaceholderEvents: boolean;
}

export const useInventoryEventBulkActionsState = (
  event?: EventWithData['event']
): UseInventoryEventBulkActionsState => {
  const { isAllSelected } = useMultiSelectionContext();

  const { filterQueryWithEventIds } = useFilterQueryState<ListingQuery>(
    ActionOutboxEntityType.Listing,
    event?.virtId ? [event.virtId] : []
  );

  const { eventsTransformed } = useCatalogDataContext();

  const listings = useMemo(() => {
    return (eventsTransformed ?? [])
      .flatMap((ev) => ev.entities.listings)
      .flatMap(flattenListingGroup)
      .filter(({ id, isLtGrp }) => id > 0 && !isLtGrp) as Listing[];
  }, [eventsTransformed]);

  const affectedVisibleListingIds = useMemo(() => {
    // Else we must respect only the entity Ids and event Ids
    const ids = [...(filterQueryWithEventIds.entityIds ?? [])];
    ids.push(
      ...listings
        .filter((l) =>
          filterQueryWithEventIds.eventOrMappingIds?.includes(l.viagVirtId)
        )
        .map((l) => l.id)
    );
    return ids;
  }, [
    filterQueryWithEventIds.entityIds,
    filterQueryWithEventIds.eventOrMappingIds,
    listings,
  ]);

  const hasSelectedPlaceholderEvents = useMemo(
    () =>
      selectionHasPlaceholderEvents(eventsTransformed, {
        selectedEventVirtIds: filterQueryWithEventIds.eventOrMappingIds,
        selectedListingIds: affectedVisibleListingIds,
      }),
    [
      eventsTransformed,
      filterQueryWithEventIds.eventOrMappingIds,
      affectedVisibleListingIds,
    ]
  );

  return {
    isAllListingsSelected: isAllSelected,
    affectedVisibleListingIds,
    filterQueryWithEventIds,
    hasSelectedPlaceholderEvents,
  };
};

/**
 * Returns whether a bulk selection contains any placeholder event.
 *
 * A selection reaches here as either directly selected event virtIds
 * (`selectedEventVirtIds`) or individual listing ids (`selectedListingIds`)
 * that we map back to their owning event via `viagVirtId`. Placeholder events
 * are synthesized and carry no listings, so in practice they only ever arrive
 * via `selectedEventVirtIds` — but we resolve both paths so the helper stays
 * correct regardless of how the selection was built.
 */
export const selectionHasPlaceholderEvents = (
  eventsTransformed: EventWithData[] | undefined,
  {
    selectedEventVirtIds,
    selectedListingIds,
  }: {
    selectedEventVirtIds?: string[] | null;
    selectedListingIds?: number[] | null;
  }
): boolean => {
  const events = eventsTransformed ?? [];
  if (events.length === 0) {
    return false;
  }

  const selectedVirtIds = new Set(selectedEventVirtIds ?? []);
  const selectedListingIdSet = new Set(selectedListingIds ?? []);
  if (selectedVirtIds.size === 0 && selectedListingIdSet.size === 0) {
    return false;
  }

  return events.some((ev) => {
    if ((ev.event.placeholders?.length ?? 0) === 0) {
      return false;
    }
    if (selectedVirtIds.has(ev.event.virtId)) {
      return true;
    }
    return (ev.entities.listings ?? []).some((l) =>
      selectedListingIdSet.has(l.id)
    );
  });
};
